import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";
import { ExportImportButtons } from "@/components/ExportImportButtons";

const CATS = ["HA only", "SHA only", "TV only", "TV/SHA", "HA/SHA", "All Appliances"];

export default function Technicians() {
  const { can } = useAuth();
  const canEdit = can("technicians.edit");
  const canAdd = can("technicians.add");
  const [techs, setTechs] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const canAddLogin = can("users.add") || can("users.add_technician");
  const blankLogin = { login_email: "", login_phone: "", login_password: "", login_password_confirm: "", create_login: true };
  const [login, setLogin] = useState(blankLogin);
  const blank = { name: "", phone: "", province: "", area: "", location: "", category: "All Appliances", active: true, latitude: null, longitude: null };
  const [form, setForm] = useState(blank);

  const load = () => { api.get("/technicians").then((r) => setTechs(r.data)); };
  useEffect(() => { load(); api.get("/masters/provinces").then((r) => setProvinces(r.data)); }, []);

  const onSave = async () => {
    try {
      if (editing) {
        await api.put(`/technicians/${editing.id}`, form);
      } else {
        const body = { ...form };
        if (login.create_login) {
          if (!login.login_password || login.login_password.length < 8) {
            toast.error("Password must be at least 8 characters"); return;
          }
          if (login.login_password !== login.login_password_confirm) {
            toast.error("Passwords do not match"); return;
          }
          if (!login.login_email && !login.login_phone && !form.phone) {
            toast.error("Login email or phone is required"); return;
          }
          body.login_email = login.login_email || null;
          body.login_phone = login.login_phone || form.phone || null;
          body.login_password = login.login_password;
          body.login_password_confirm = login.login_password_confirm;
        }
        await api.post("/technicians", body);
      }
      toast.success(editing ? "Saved" : (login.create_login ? "Technician and login created" : "Saved"));
      setOpen(false); setEditing(null); setForm(blank); setLogin(blankLogin); load();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this technician?")) return;
    await api.delete(`/technicians/${id}`); toast.success("Deleted"); load();
  };
  const openEdit = (t) => { setEditing(t); setForm({ ...blank, ...t }); setOpen(true); };

  return (
    <div className="space-y-5" data-testid="technicians-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Technicians</h1>
          <p className="text-sm text-slate-500 mt-1">{techs.length} technicians in master list.</p>
        </div>
        <div className="flex gap-2 items-start">
          <ExportImportButtons kind="technicians" label="Technicians" onDone={load} />
        {canAdd && (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-tech-btn"><Plus size={14} className="mr-1" /> Add Technician</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Technician</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tech-name-input" /></Fld>
              <Fld label="Phone"><Input value={form.phone||""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Fld>
              <Fld label="Province">
                <Select value={form.province || ""} onValueChange={(v) => setForm({ ...form, province: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{provinces.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Area"><Input value={form.area||""} onChange={(e) => setForm({ ...form, area: e.target.value })} /></Fld>
              <Fld label="Location"><Input value={form.location||""} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Fld>
              <Fld label="Category">
                <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Latitude"><Input type="number" step="any" value={form.latitude ?? ""} onChange={(e) => setForm({ ...form, latitude: e.target.value === "" ? null : parseFloat(e.target.value) })} /></Fld>
              <Fld label="Longitude"><Input type="number" step="any" value={form.longitude ?? ""} onChange={(e) => setForm({ ...form, longitude: e.target.value === "" ? null : parseFloat(e.target.value) })} /></Fld>
              <Fld label="Active">
                <Select value={String(form.active)} onValueChange={(v) => setForm({ ...form, active: v === "true" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
            </div>
            {!editing && canAddLogin && (
              <div className="mt-4 border-t pt-3" data-testid="login-access-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-900">Login Access</div>
                  <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                    Role: Technician
                  </span>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={login.create_login}
                         onChange={(e) => setLogin({ ...login, create_login: e.target.checked })}
                         data-testid="tech-create-login-checkbox" />
                  Create login user so this technician can sign in
                </label>
                {login.create_login && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <Fld label="Login Email (optional)">
                      <Input type="email" value={login.login_email}
                             onChange={(e) => setLogin({ ...login, login_email: e.target.value })}
                             data-testid="tech-login-email"
                             placeholder="leave blank to login by phone" />
                    </Fld>
                    <Fld label="Login Phone / Username">
                      <Input value={login.login_phone}
                             onChange={(e) => setLogin({ ...login, login_phone: e.target.value })}
                             placeholder={form.phone || "98xxxxxxxx"}
                             data-testid="tech-login-phone" />
                    </Fld>
                    <Fld label="Password">
                      <Input type="password" value={login.login_password}
                             onChange={(e) => setLogin({ ...login, login_password: e.target.value })}
                             data-testid="tech-login-password" />
                    </Fld>
                    <Fld label="Confirm Password">
                      <Input type="password" value={login.login_password_confirm}
                             onChange={(e) => setLogin({ ...login, login_password_confirm: e.target.value })}
                             data-testid="tech-login-password-confirm" />
                    </Fld>
                    <div className="col-span-2 text-[11px] text-slate-500">
                      Role is locked to <span className="font-semibold">Technician</span>. The technician can log in with email OR phone (minimum 8-character password).
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={onSave} className="bg-slate-900 text-white" data-testid="save-tech-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
        </div>
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full dense-table">
            <thead className="bg-slate-50 border-b"><tr>
              <Th>Name</Th><Th>Phone</Th><Th>Province</Th><Th>Area</Th><Th>Category</Th><Th>Status</Th><Th align="right">Actions</Th>
            </tr></thead>
            <tbody>
              {techs.map((t) => (
                <tr key={t.id} data-testid={`tech-row-${t.id}`}>
                  <td className="font-medium text-slate-900">{t.name}</td>
                  <td className="font-mono">{t.phone}</td>
                  <td>{t.province}</td>
                  <td>{t.area}</td>
                  <td><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-semibold">{t.category}</span></td>
                  <td>{t.active ? <span className="text-emerald-700 font-semibold text-xs">ACTIVE</span> : <span className="text-slate-400 text-xs">INACTIVE</span>}</td>
                  <td className="text-right">
                    {canEdit ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-tech-${t.id}`}><Edit size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onDelete(t.id)} data-testid={`delete-tech-${t.id}`}><Trash2 size={14} /></Button>
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}
function Fld({ label, children }) {
  return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>{children}</div>;
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold`}>{children}</th>;
}
